import React from "react";
import PropTypes from "prop-types";
import { Link, useLocation } from "react-router-dom";
import { getSection } from "../config/sections";
import "./TitlePages.css";

const TitlePages = ({
  section = "",
  title = "",
  subsections = [],
  // Propiedades legadas o alternativas
  parent = null,
  parentSection = "",
  parentUrl = "",
  description = "",
  subTitle = "",
  hideBreadcrumbs = false,
  className = "",
  hideTitle = false,
}) => {
  const location = useLocation();
  const currentPath = location && location.pathname ? location.pathname : "";

  // 1. Resolver sección
  // Si se pasa 'section', buscar en el mapa central.
  // Si no se pasa, intentar con parent/parentSection o auto-detectar por ruta actual.
  let sectionKeyOrPath = section || parent || parentSection || currentPath;
  let sectionObj = null;

  if (typeof sectionKeyOrPath === "object" && sectionKeyOrPath !== null) {
    sectionObj = {
      name: sectionKeyOrPath.name || sectionKeyOrPath.label || "",
      url: sectionKeyOrPath.url || "",
    };
  } else if (typeof sectionKeyOrPath === "string" && sectionKeyOrPath.trim() !== "") {
    sectionObj = getSection(sectionKeyOrPath);
    // Si no se encontró en el mapa pero se especificó parent o parentSection con URL manual
    if (!sectionObj && (parent || parentSection)) {
      sectionObj = {
        name: parent || parentSection,
        url: parentUrl || "",
      };
    }
  }

  // 2. Normalizar subsecciones intermedias (si existen)
  let intermediateLevels = [];
  if (Array.isArray(subsections)) {
    intermediateLevels = subsections;
  } else if (subsections) {
    intermediateLevels = [subsections];
  }

  const normalizedSubsections = intermediateLevels
    .filter(Boolean)
    .map((item) => {
      if (typeof item === "string") {
        return { label: item, url: null };
      }
      return {
        label: item.name || item.label || item.title || "",
        url: item.url || item.path || null,
      };
    })
    .filter((sub) => sub.label && sub.label.trim() !== "");

  const hasSubsections = normalizedSubsections.length > 0;

  const shortDesc = description || subTitle;

  // Si no se proporciona un título principal pero sí un subTitle/description,
  // renderizar exclusivamente como subtítulo de sección interno con su propio estilo,
  // NUNCA repitiendo las migas de pan ni el contenedor hero.
  if (!title || title.trim() === "") {
    if (!shortDesc || shortDesc.trim() === "") {
      return null;
    }
    return (
      <div className={`page-subtitle-block ${className}`.trim()}>
        <h2 className="page-subtitle-title">{shortDesc}</h2>
        <div className="page-subtitle-line" aria-hidden="true" />
      </div>
    );
  }

  // Si se ocultó el título explícitamente
  if (hideTitle) {
    return null;
  }

  // Evitar repetir "Transparencia" como título principal en grande cuando existe un subTitle o descripción:
  // El texto específico (ej. "Cuenta Pública", "Infografías", "Preguntas Frecuentes", "Datos Abiertos", "Convenios", etc.)
  // pasa a ser el título principal h1, eliminando la redundancia.
  let displayTitle = title;
  let displayDesc = shortDesc;

  if (
    title &&
    title.trim().toLowerCase() === "transparencia" &&
    shortDesc &&
    shortDesc.trim() !== ""
  ) {
    displayTitle = shortDesc;
    displayDesc = "";
  }

  return (
    <header className={`page-hero ${className}`.trim()}>
      {/* Círculo radial blur detrás del título */}
      <div className="page-hero-glow" aria-hidden="true"></div>

      <div className="page-hero-container">
        {/* 1. Migas de pan generadas automáticamente */}
        {!hideBreadcrumbs && (
          <nav aria-label="Migas de pan" className="page-hero-breadcrumbs">
            <ol className="page-hero-breadcrumbs-list">
              {/* Nivel raíz: Inicio */}
              <li className="page-hero-breadcrumb-item">
                <Link to="/" className="page-hero-breadcrumb-link">
                  Inicio
                </Link>
              </li>

              {/* Nivel de sección (del mapa central) */}
              {sectionObj && sectionObj.name && (
                <>
                  <span
                    className="page-hero-breadcrumb-separator"
                    aria-hidden="true"
                  >
                    &gt;
                  </span>
                  <li className="page-hero-breadcrumb-item">
                    {hasSubsections ? (
                      // Si hay subsecciones intermedias posteriores, la sección es un enlace intermedio
                      sectionObj.url ? (
                        sectionObj.url.startsWith("http") ? (
                          <a
                            href={sectionObj.url}
                            className="page-hero-breadcrumb-link"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {sectionObj.name}
                          </a>
                        ) : (
                          <Link
                            to={sectionObj.url}
                            className="page-hero-breadcrumb-link"
                          >
                            {sectionObj.name}
                          </Link>
                        )
                      ) : (
                        <span className="page-hero-breadcrumb-link">
                          {sectionObj.name}
                        </span>
                      )
                    ) : (
                      // Si no hay subsecciones, la sección es el último nivel de las migas (#AD0A85)
                      sectionObj.url ? (
                        sectionObj.url.startsWith("http") ? (
                          <a
                            href={sectionObj.url}
                            className="page-hero-breadcrumb-parent"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {sectionObj.name}
                          </a>
                        ) : (
                          <Link
                            to={sectionObj.url}
                            className="page-hero-breadcrumb-parent"
                          >
                            {sectionObj.name}
                          </Link>
                        )
                      ) : (
                        <span className="page-hero-breadcrumb-parent">
                          {sectionObj.name}
                        </span>
                      )
                    )}
                  </li>
                </>
              )}

              {/* Subsecciones intermedias (opcionales) */}
              {normalizedSubsections.map((sub, index) => {
                const isLastSub = index === normalizedSubsections.length - 1;
                return (
                  <React.Fragment key={index}>
                    <span
                      className="page-hero-breadcrumb-separator"
                      aria-hidden="true"
                    >
                      &gt;
                    </span>
                    <li className="page-hero-breadcrumb-item">
                      {isLastSub ? (
                        sub.url ? (
                          sub.url.startsWith("http") ? (
                            <a
                              href={sub.url}
                              className="page-hero-breadcrumb-parent"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {sub.label}
                            </a>
                          ) : (
                            <Link
                              to={sub.url}
                              className="page-hero-breadcrumb-parent"
                            >
                              {sub.label}
                            </Link>
                          )
                        ) : (
                          <span className="page-hero-breadcrumb-parent">
                            {sub.label}
                          </span>
                        )
                      ) : (
                        sub.url ? (
                          sub.url.startsWith("http") ? (
                            <a
                              href={sub.url}
                              className="page-hero-breadcrumb-link"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {sub.label}
                            </a>
                          ) : (
                            <Link
                              to={sub.url}
                              className="page-hero-breadcrumb-link"
                            >
                              {sub.label}
                            </Link>
                          )
                        ) : (
                          <span className="page-hero-breadcrumb-link">
                            {sub.label}
                          </span>
                        )
                      )}
                    </li>
                  </React.Fragment>
                );
              })}
            </ol>
          </nav>
        )}

        {/* 2. Título h1 directamente bajo las migas */}
        {displayTitle && (
          <div className="page-hero-title-container">
            <h1 className="page-hero-title" aria-current="page">
              {displayTitle}
            </h1>
          </div>
        )}

        {/* Línea de acento proporcional bajo el título */}
        <div className="page-hero-accent-line" aria-hidden="true"></div>

        {/* Descripción corta opcional */}
        {displayDesc && displayDesc.trim() !== "" && (
          <p className="page-hero-description">{displayDesc}</p>
        )}
      </div>
    </header>
  );
};

export const PageHero = TitlePages;
export default TitlePages;

TitlePages.propTypes = {
  section: PropTypes.string,
  title: PropTypes.string,
  subsections: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
          name: PropTypes.string,
          label: PropTypes.string,
          title: PropTypes.string,
          url: PropTypes.string,
          path: PropTypes.string,
        }),
      ])
    ),
    PropTypes.string,
    PropTypes.shape({
      name: PropTypes.string,
      label: PropTypes.string,
      title: PropTypes.string,
      url: PropTypes.string,
      path: PropTypes.string,
    }),
  ]),
  parent: PropTypes.oneOfType([
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      url: PropTypes.string,
    }),
    PropTypes.string,
  ]),
  parentSection: PropTypes.string,
  parentUrl: PropTypes.string,
  description: PropTypes.string,
  subTitle: PropTypes.string,
  hideBreadcrumbs: PropTypes.bool,
  className: PropTypes.string,
  hideTitle: PropTypes.bool,
};

export const SubTitle = ({ subTitle = "", title = "", className = "" }) => {
  const text = subTitle || title;
  if (!text || text.trim() === "") return null;
  return (
    <div className={`page-subtitle-block ${className}`.trim()}>
      <h2 className="page-subtitle-title">{text}</h2>
      <div className="page-subtitle-line" aria-hidden="true" />
    </div>
  );
};

export const Title = ({ title = "", className = "" }) => {
  if (!title || title.trim() === "") return null;
  return <TitlePages title={title} className={className} />;
};
